<template>
  <b-tabs pills content-class="mt-2 mb-4" v-if="value" v-model="tabIndex">
    <loading-modal ref="loadingModal" />

    <!--
    ============================================================================
    TAB Generale
    ============================================================================
    -->
    <b-tab>
      <template slot="title">
        Generale
        <b-badge variant="danger"
          v-if="countErrors(['title', 'description'])">
          {{ countErrors(['title', 'description']) }}
        </b-badge>
      </template>
      <form>
        <user-form-field
          label="Proprietario"
          required
          :value="value.owner"
          disabled
          :error="findError('owner')"
        />

        <workgroup-form-field
          label="Gruppo di lavoro"
          required
          :value="value.analysis.workgroup"
          disabled
        />

        <string-form-field
          label="Titolo"
          required
          :value="value.title"
          :disabled="disabled"
          :error="findError('title')"
          @input="propertyChanged('title', $event)"
        />

        <string-form-field
          label="Descrizione"
          :value="value.description"
          :disabled="disabled"
          :error="findError('description')"
          multiline
          @input="propertyChanged('description', $event)"
        />
      </form>
    </b-tab>
    <!--
    ============================================================================
    TAB Bilanci
    ============================================================================
    -->
    <b-tab>
      <template slot="title">
        Bilanci
        <b-badge variant="danger"
          v-if="countErrors('uploadedFiles')">
          {{ countErrors('uploadedFiles') }}
        </b-badge>
      </template>
      <!--
      ==========================================================================
      TAB Bilanci > Caricamento
      ==========================================================================
      -->
      <template v-if="!disabled">
        <b-tabs card pills style="margin-top: -1rem; margin-left: -1rem; margin-right: -1rem;">
          <b-tab title="Manuale" active>
            <file-table
              class="mb-4"
              ref="fileTable"
              restrict="*.xbrl"
              label="Caricare qui i bilanci xbrl, trascinando i file nel campo sottostante o utilizzando l'apposito pulsante."
              :value="pendingFiles"
              @input="pendingFiles = $event"
              @upload="upload($event)">
              <b-alert slot="advice" show variant="info">
                <strong>N.B.</strong> Il nome di ciascun file deve terminare con
                <strong>.xbrl</strong>, altrimenti il file verrà scartato. Qualora
                si vogliano caricare bilanci xbrl con estensione diversa (ad esempio
                <strong>.xml</strong>), si prega di rinominare i file prima del
                caricamento.
              </b-alert>
            </file-table>
          </b-tab>
          <b-tab title="Automatico">
            <b-alert show variant="info">
              <strong>N.B.</strong> Questa funzionalità permette di ottenere automaticamente
              un bilancio dalla CCIAA. <strong>I bilanci richiesti tramite questa opzione
              sono a pagamento.</strong> Il software registrerà le richieste effettuate
              dall'utente per la rendicontazione. I campi relativi alla provincia della
              CCIAA e al numero REA sono ottenuti automaticamente dall'analisi principale e,
              solo se presenti, permettono di richiedere il file desiderato.
            </b-alert>
            <form>
              <div class="row">
                <string-form-field
                  class="col-md-4"
                  label="Provincia CCIAA"
                  required
                  :value="xbrlFileRequest.cciaa"
                  disabled
                /><!--
                :error="findError('owner')"-->

                <string-form-field
                  class="col-md-4"
                  label="Numero REA"
                  required
                  :value="xbrlFileRequest.rea"
                  disabled
                />

                <number-form-field
                  class="col-md-4"
                  label="Anno"
                  required
                  v-model="xbrlFileRequest.year"
                  :disabled="disabled"
                  :precision="0"
                  thousand-separator=""
                />
              </div>
              <div class="text-right">
                <b-button
                  variant="success"
                  :disabled="!xbrlFileRequest.cciaa
                      || !xbrlFileRequest.rea
                      || !xbrlFileRequest.year
                      || xbrlFileRequest.year < 2000
                      || xbrlFileRequest.year > currentYear()"
                  @click.stop.prevent="getFinancialStatements()">
                  <i class="fa fa-fw fa-download"></i>
                  Richiedi
                </b-button>
              </div>
            </form>
          </b-tab>
        </b-tabs>
        <div v-if="value.uploadedFiles.length" class="content-separator"></div>
      </template>

      <!--
      ==========================================================================
      TAB Bilanci > Bilanci caricati
      ==========================================================================
      -->
      <div v-if="value.uploadedFiles.length">
        <data-table
          :columns="uploadedFilesColumns"
          :value="value.uploadedFiles"
        />

        <b-alert show variant="info">
          <strong>N.B.</strong> Per molte analisi sono necessari bilanci
          ordinari: bilanci abbreviati o in altre forme potrebbero non
          contenere tutti i dati necessari.
          Cliccando sul pulsante <strong>Estrai</strong>, il software estrarrà
          i dati dai file caricati, cercando di adattare automaticamente i vari
          tipi di bilancio alla forma ordinaria, tuttavia è consigliabile
          controllare i dati estratti per verificarne la correttezza/completezza
          prima di selezionare il tab <strong>Analisi</strong> e di procedere
          oltre.
        </b-alert>

        <div class="text-right">
          <b-button
            v-if="!disabled"
            variant="success"
            @click.stop.prevent="extractData()">
            <i class="fa fa-fw fa-area-chart"></i>
            Estrai
          </b-button>
        </div>
      </div>

      <b-modal
        ref="extractionAdvice"
        size="lg"
        scrollable
        ok-only
        title="Estrazione dati completata con successo.">
        <p>
          Durante l'estrazione il software ha adattato automaticamente
          ogni tipo di bilancio non ordinario (es. bilancio abbreviato,
          consolidato, microimprese) alla forma standard di bilancio ordinario,
          tuttavia è consigliabile controllare i dati estratti per verificarne
          la correttezza/completezza.
        </p>

        <p>
          Una volta controllati/completati i dati in questo tab, si può
          selezionare il tab <strong>Analisi</strong> e procedere
          con l'elaborazione dei dati.
        </p>
        <p>
          <strong>N.B.: I dati modificati manualmente NON vengono salvati
          cliccando sul pulsante Salva a meno che non sia stata eseguita
          un'analisi con i dati modificati.</strong> Questo per evitare che
          chi apre l'analisi in futuro possa essere tratto in inganno da dati
          di input diversi da quelli realmente utilizzati.
        </p>
      </b-modal>

      <b-modal
        ref="tagsModal"
        :title="editColumn && editColumn.value.translation"
        size="lg"
        @close="editColumn = null"
        @cancel="editColumn = null"
        @ok="confirmEditColumn">
        <options-form-field
          label="Origine"
          :options="[
            { text: 'Sconosciuta', value: null    , disabled: true },
            { text: 'XBRL'       , value: 'xbrl'  ,                },
            { text: 'Manuale'    , value: 'manual',                },
          ]"
          :value="sourceFor(editColumn)"
          @input="editColumnChanged('source', $event)"
        />
        <options-form-field
          label="Tipo"
          :options="[
            { text: 'Sconosciuto' , value: null          , disabled: true },
            { text: 'Depositato'  , value: 'depositato'  ,                },
            { text: 'Provvisorio' , value: 'provvisorio' ,                },
            { text: 'Previsionale', value: 'previsionale',                },
          ]"
          :value="typeFor(editColumn)"
          @input="editColumnChanged('type', $event)"
        />
      </b-modal>

      <template v-if="input && input.length">
        <div class="content-separator my-4"></div>
        <div class="text-right">
          <b-dropdown variant="warning" v-if="!disabled">
            <template slot="button-content">
              <i class="fa fa-fw fa-plus"></i>
              Colonne
            </template>
            <b-dropdown-item-button @click.stop.prevent="addColumnLeft()">
              Aggiungi colonna a sinistra
            </b-dropdown-item-button>
            <b-dropdown-item-button @click.stop.prevent="addColumnRight()">
              Aggiungi colonna a destra
            </b-dropdown-item-button>
            <b-dropdown-item-button @click.stop.prevent="setAllTableMissingValues()">
              Imposta a 0 tutti i valori mancanti
            </b-dropdown-item-button>
          </b-dropdown>
          <b-dropdown variant="primary">
            <template slot="button-content">
              <i class="fa fa-fw fa-sign-out"></i>
              Esporta
            </template>
            <b-dropdown-item-button @click.stop.prevent="exportData('xlsx')">
              Excel (xlsx)
            </b-dropdown-item-button>
            <b-dropdown-item-button @click.stop.prevent="exportData('csv')">
              Testo (csv)
            </b-dropdown-item-button>
          </b-dropdown>
        </div>
        <b-tabs pills content-class="mt-4 mb-4 mx-0 px-0 border-0">
          <template v-for="r in input">
            <b-tab :title="r.title" class="py-0 px-0 mx-0">
              <custom-table-report
                :value="r.table"
                :disabled="disabled"
                :class-for="inputClassFor"
                :column-actions="inputTableColumnActionsFor(r.title)"
                @input="inputChanged(r.title, $event)"
                @tag-click="inputTagClick(r.title, ...arguments)"
              />
            </b-tab>
          </template>
        </b-tabs>
      </template>
    </b-tab>
    <!--
    ============================================================================
    TAB Analisi
    ============================================================================
    -->
    <b-tab v-if="value.input">
      <template slot="title">
        Analisi
      </template>

      <b-alert variant="danger" show v-if="!output || !output.length">
        <strong>Attenzione:</strong> nessuna analisi ancora eseguita.
      </b-alert>
      <b-alert variant="danger" show v-else-if="dirtyInput">
        <strong>Attenzione:</strong> dati di input modificati. Rieseguire l'analisi.
      </b-alert>

      <div class="text-right">
        <b-dropdown
          v-if="output && output.length"
          variant="primary">
          <template slot="button-content">
            <i class="fa fa-fw fa-sign-out"></i>
            Esporta
          </template>
          <b-dropdown-item-button @click.stop.prevent="exportAnalysis(analysisTypeId, 'xlsx')">
            Excel (xlsx)
          </b-dropdown-item-button>
          <b-dropdown-item-button @click.stop.prevent="exportAnalysis(analysisTypeId, 'csv')">
            Testo (csv)
          </b-dropdown-item-button>
        </b-dropdown>
        <b-dropdown
          v-if="!disabled"
          variant="success">
          <template slot="button-content">
            <i class="fa fa-fw fa-area-chart"></i>
            Analizza
          </template>
          <b-dropdown-item-button
            v-if="!output || !output.length || dirtyInput"
            @click.stop.prevent="analyze(analysisTypeId)">
            Analisi semplice
          </b-dropdown-item-button>
          <b-dropdown-item-button
            @click.stop.prevent="analyze(analysisTypeId, true)">
            Analisi forzata
          </b-dropdown-item-button>
        </b-dropdown>
      </div>

      <b-modal
        ref="analysisAdvice"
        size="lg"
        scrollable
        ok-only
        title="Analisi completata con successo.">
        <p>
          Il software evidenzia le celle con dati non calcolabili in
          <span class="border border-danger p-1">rosso</span>.
        </p>
        <p>
          Qualora siano presenti errori di questo tipo, si raccomanda
          di tornare al tab <strong>Bilanci</strong> e verificare i dati di
          input.
        </p>
        <p>
          In particolare, nel tab <strong>Bilanci</strong>, tutti gli input
          necessari ma mancanti, sono evidenziti in
          <span class="border border-danger p-1">rosso</span>. Questi debbono
          essere necessariamente inseriti.
        </p>
        <p>
          Gli input presenti ma pari a 0 sono evidenziti in
          <span class="border border-warning p-1">giallo</span>. Questi non
          rappresentano necessariamente un errore, ma si consiglia di
          ricontrollare.
        </p>
        <p>
          Una volta completato il controllo dei dati di input, si potrà
          procedere ad una nuova elaborazione.
        </p>
      </b-modal>

      <b-tabs
        v-if="output && output.length"
        pills
        content-class="mt-4 mb-4 mx-0 px-0 border-0">
        <template v-for="r in output">
          <b-tab
            :title="r.title"
            class="py-0 px-0 mx-0">
            <custom-table-report
              :value="r.table"
              disabled
              no-tags
            />
          </b-tab>
        </template>
      </b-tabs>
    </b-tab>
    <!--
    ============================================================================
    TAB Storico
    ============================================================================
    -->
    <b-tab v-if="value.outputHistory">
      <template slot="title">
        Storico
      </template>
      <b-row>
        <b-col class="px-4">
          <data-table
            :columns="extractionHistoryColumns"
            :value="value.extractionHistory"
            title="Storico estrazioni"
            empty-message="Nessuna estrazione ancora presente."
          />
        </b-col>
        <b-col class="px-4">
          <data-table
            :columns="outputHistoryColumns"
            :value="value.outputHistory"
            title="Storico analisi"
            empty-message="Nessuna analisi ancora presente."
          />
        </b-col>
      </b-row>
    </b-tab>
  </b-tabs>
</template>
<script>
import XbrlAnalysisService from 'app/services/XbrlAnalysisService';
import { error2text } from 'core/utils';
import BaseAnalysisEditorMixin from 'app/mixins/BaseAnalysisEditorMixin';

export default {
  mixins: [ BaseAnalysisEditorMixin ],

  created() {
    this.service = XbrlAnalysisService;

    this.uploadedFilesColumns = [
      ...this.uploadedFilesPreColumns,

      { label: 'Anno'          , value: (item) => item.year, style: 'max-width: 70px;' },
      { label: 'Tipo'          , value: (item) => this.labelForType(item.type) },
      { label: 'Codice fiscale', value: (item) => item.datiAnagrafici.codiceFiscale },
      { label: 'Partita IVA'   , value: (item) => item.datiAnagrafici.partitaIva },
      { label: 'Denominazione' , value: (item) => item.datiAnagrafici.denominazione, class: 'col-2' },
      //{ label: 'CCIAA'         , value: (item) => item.datiAnagrafici.codiceCciaa },
      //{ label: 'REA'           , value: (item) => item.datiAnagrafici.numeroRea },
      { label: 'Dimensione'    , value: (item) => item.filesize, type: 'filesize' },
      {
        label: 'Fornitore',
        type: 'component',
        component: 'options-form-field',
        class: 'col mb-0',
        props: (item, index) => ({
          options: [
            { value: null, text: '' },
            'CCIAA',
            'Commercialista',
            'Altro'
          ],
          dropdown: true,
          disabled: this.disabled,
          value: item.source,
          error: this.findError('uploadedFiles[' + index + '].source')
        }),
        events: (item, index) => ({
          input: (v) => {
            const uploadedFiles = [ ...this.value.uploadedFiles ];
            uploadedFiles[index] = { ...item, source: v };
            this.propertyChanged('uploadedFiles', uploadedFiles);
          }
        })
      },

      ...this.uploadedFilesPostColumns
    ];
  },

  data() {
    return {
      analysisTypeId: 1,
      editColumn: null,
      xbrlFileRequest: {
        cciaa: this.value.analysis.cciaaCode,
        rea: this.value.analysis.reaNumber,
        year: null
      }
    };
  },

  computed: {
    variables() {
      return this.value.variables || [];
    },

    input() {
      const data = this.value.input;
      const ret = [];
      if (data) {
        for (let k of Object.keys(data)) {
          ret.push({ title: k, table: data[k] });
        }
      }
      return ret;
    },

    output() {
      const data = this.value.output;
      const ret = [];
      if (data) {
        for (let k of Object.keys(data)) {
          ret.push({ title: k, table: data[k] });
        }
      }
      return ret;
    }
  },

  methods: {
    inputTagClick(tableName, col, colIndex, tag, tagIndex) {
      //console.log(col, colIndex, tag, tagIndex);
      this.editColumn = {
        tableName,
        //index: colIndex,
        value: { ...col }
      };

      this.$refs.tagsModal.show();
    },

    sourceFor(editColumn) {
      const tag = editColumn
          && editColumn.value.tags.find(item => item && item.startsWith('source:'));
      if (tag) {
        return tag.substring('source:'.length);
      }
      return undefined;
    },

    typeFor(editColumn) {
      const tag = editColumn
          && editColumn.value.tags.find(item => item && item.startsWith('type:'));
      if (tag) {
        return tag.substring('type:'.length);
      }
      return undefined;
    },

    editColumnChanged(tagPrefix, v) {
      if (this.editColumn) {
        const editColumn = { ...this.editColumn };
        const value = { ...editColumn.value };
        const tags = value.tags.filter(item => !item.startsWith(tagPrefix));
        if (v) {
          tags.push(tagPrefix + ':' + v);
        }
        value.tags = tags;
        editColumn.value = value;
        this.editColumn = editColumn;
      }
    },

    confirmEditColumn() {
      const input = { ...this.value.input };
      const table = { ...input[this.editColumn.tableName] };
      const columns = { ...table.columns };
      columns[this.editColumn.value.name] = this.editColumn.value;
      table.columns = columns;
      input[this.editColumn.tableName] = table;
      this.editColumn = null;
      this.propertyChanged('input', input);
    },

    addColumnLeft(num = 1) {
      const input = this.value.input;
      if (input) {
        const newInput = { ...input };
        for (let k of Object.keys(input)) {
          const table = input[k];
          const columns = Object.keys(table.columns);
          const firstColumn = columns[0];
          const firstYear = parseInt(firstColumn, 10);
          const newColumns = {};
          for (let i = num; i > 0; i--) {
            const previousYear    = firstYear - i;
            const previousYearStr = '' + previousYear;
            newColumns[previousYearStr] = {
              name: previousYearStr,
              translation: previousYearStr,
              tags: [ "source:manual" ]
            };
          }
          const newTable = {
            ...table,
            columns: {
              ...newColumns,
              ...table.columns
            }
          };
          newInput[k] = newTable;
        }
        this.dirtyInput = true;
        this.propertyChanged('input', newInput);
      }
    },

    addColumnRight(num = 1) {
      const input = this.value.input;
      if (input) {
        const newInput = { ...input };
        for (let k of Object.keys(input)) {
          const table = input[k];
          const columns = Object.keys(table.columns);
          const lastColumn = columns[columns.length - 1];
          const lastYear = parseInt(lastColumn, 10);
          const newColumns = {};
          for (let i = 1; i <= num; i++) {
            const nextYear    = lastYear + i;
            const nextYearStr = '' + nextYear;
            newColumns[nextYearStr] = {
              name: nextYearStr,
              translation: nextYearStr,
              tags: [ "source:manual" ]
            };
          }
          const newTable = {
            ...table,
            columns: {
              ...newColumns,
              ...table.columns
            }
          };
          newInput[k] = newTable;
        }
        this.dirtyInput = true;
        this.propertyChanged('input', newInput);
      }
    },

    inputClassFor(row, col, v) {
      const ret = {
        'text-right': true,
        'font-weight-bold': row.options.bold,
        'shadow-none': true,
      };

      let border = false;
      if (this.variables.find(item => item === row.name)) {
        if (v === null || v === undefined || isNaN(v)) {
          ret['border-danger'] = true;
          border = true;
        } else if (v === 0) {
          ret['border-warning'] = true;
          border = true;
        } else if (v < 0) {
          ret['text-danger'] = true;
        }
      }

      if (border) {
        if (this.disabled) {
          ret['border rounded px-2'] = true;
        }
      } else {
        ret['border-0 px-2'] = true;
      }

      return ret;
    },

    inputChanged(tableTitle, table) {
      const input = { ...this.value.input };
      input[tableTitle] = table;
      this.dirtyInput = true;
      this.propertyChanged('input', input);
    },

    inputTableColumnActionsFor(tableTitle) {
      if (this.disabled) {
        return null;
      }
      const actions = [
        {
          label: 'Imposta a 0 i valori mancanti',
          action: (col, index) => {
            //console.log('Imposta a 0 i valori mancanti', col);
            const input = { ...this.value.input };
            const table = { ...input[tableTitle] };
            const values = { ...table.values };
            this.setTableChildren(table, values, col, table.children);
            input[tableTitle] = { ...table, values: values };
            this.dirtyInput = true;
            this.propertyChanged('input', input);
          }
        },
      ];

      if (this.value.permissions.removeColumns) {
        actions.push({
          label: 'Elimina questa colonna',
          action: (col, index) => {
            const colName = col.name;
            const input = { ...this.value.input };

            const tableNames = Object.keys(input);
            for (let tn of tableNames) {
              const table = { ...input[tn] };
              const columns = { ...table.columns };
              const values = { ...table.values };
              for (let k in values) {
                const row = { ...values[k] };
                delete row[colName];
                values[k] = row;
              }
              delete columns[colName];
              input[tn] = {
                ...table,
                columns,
                values
              };
            }

            this.dirtyInput = true;
            this.propertyChanged('input', input);
          }
        });
      }

      return actions;
    },

    setAllTableMissingValues() {
      const input = { ...this.value.input };
      for (let tableTitle of Object.keys(input)) {
        const table = { ...input[tableTitle] };
        const values = { ...table.values };
        for (let col of Object.values(table.columns)) {
          this.setTableChildren(table, values, col, table.children);
        }
        input[tableTitle] = { ...table, values: values };
      }
      this.dirtyInput = true;
      this.propertyChanged('input', input);
    },

    setTableChildren(table, values, col, children) {
      for (let row of children) {
        if (row.name && (!row.children || !row.children.length)) {
          if (values[row.name][col.name] === undefined || values[row.name][col.name] === null) {
            const rowValues = { ...values[row.name] };
            rowValues[col.name] = 0;
            values[row.name] = rowValues;
          }
        }
        this.setTableChildren(table, values, col, row.children);
      }
    },

    // =========================================================================
    // Utils
    // =========================================================================

    labelForType(type) {
      if (type === 'ese' || type === '') {
        return 'Ordinario';
      } else if (type === 'abb') {
        return 'Abbreviato';
      } else if (type === 'cons') {
        return 'Consolidato';
      } else if (type === 'micr') {
        return 'Microimprese';
      }
    },

    // =========================================================================
    // Extraction
    // =========================================================================

    loadUserInput(auxData) {
      // console.log('Going back to extraction');
      const success = ({ statusCode, data }) => {
        console.log('Extraction success:', statusCode, data);
        this.loading = false;
        this.dirtyInput = true;
        this.$refs.extractionAdvice.show();

        const obj = { ...this.value };
        if (data.input) {
          obj.input = data.input;
        }
        if (auxData.extractionHistory) {
          obj.extractionHistory = auxData.extractionHistory;
        }
        this.$emit('input', obj);
      }

      const error = ({ statusCode, data }) => {
        console.log('Extraction error:', statusCode, data);
        this.loading = false;
        alert(error2text(data));
      }

      this.loading = true;
      XbrlAnalysisService.loadUserInput({
        analysisId: this.value.analysis.id,
        tempId: this.value.tempId,
      }).then(success, error);

      return false;
    },

    onExtractDataSuccess({ statusCode, data }) {
      console.log('Extraction success:', statusCode, data);
      this.loading = false;

      let loadUserInput = false;
      if (this.value.outputHistory && this.value.outputHistory.length) {
        loadUserInput = confirm('Vuoi sovrascrivere i valori estratti '
           + 'con quelli inseriti manualmente per l\'analisi precedente?');
      }
      if (loadUserInput) {
        this.loadUserInput(data);
      } else {
        this.dirtyInput = true;
        this.$refs.extractionAdvice.show();

        const obj = { ...this.value };
        if (data.input) {
          obj.input = data.input;
        }
        if (data.extractionHistory) {
          obj.extractionHistory = data.extractionHistory;
        }
        this.$emit('input', obj);
      }
    },
    // =========================================================================
    // Analysis
    // =========================================================================

    onAnalyzeSuccess({ statusCode, data }) {
      console.log('Analysis success:', statusCode, data);
      this.loading = false;
      this.dirtyInput = false;
      this.$refs.analysisAdvice.show();

      const obj = { ...this.value };
      if (data.output) {
        obj.output = data.output;
      }
      if (data.variables) {
        obj.variables = data.variables;
      }
      if (data.outputHistory) {
        obj.outputHistory = data.outputHistory;
      }
      this.$emit('input', obj);
    },

    onGoBackToAnalysisSuccess({ statusCode, data }) {
      console.log('Extraction success:', statusCode, data);
      this.loading = false;
      this.dirtyInput = false;
      //this.$refs.extractionAdvice.show();

      const obj = { ...this.value };
      if (data.input) {
        obj.input = data.input;
      }
      if (data.output) {
        obj.output = data.output;
      }
      if (data.outputHistory) {
        obj.outputHistory = data.outputHistory;
      }
      if (data.variables) {
        obj.variables = data.variables;
      }
      this.$emit('input', obj);

      this.tabIndex = 2;
    },

    getFinancialStatements() {
      const success = ({ statusCode, data }) => {
        const uploadedFiles = [ ...this.value.uploadedFiles ];
        uploadedFiles.push(data);
        this.propertyChanged('uploadedFiles', uploadedFiles);
        this.loading = false;
      }

      const error = ({ statusCode, data }) => {
        console.log('getFinancialStatements() error:', statusCode, data);
        alert(error2text(data));
        this.loading = false;
      }

      this.loading = true;
      const prom = this.service.getFinancialStatements({
        analysisId: this.value.analysis.id,
        tempId: this.value.tempId,
        data: this.xbrlFileRequest
      }).then(success, error);
    },

    currentYear() {
      return new Date().getFullYear();
    }
  }
}
</script>
